import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Paper, Chip, CircularProgress, Alert, Button, Avatar, Stack, Grid, TextField, FormControl, InputLabel, Select, MenuItem, OutlinedInput } from '@mui/material';
import { taskService } from '../../services/taskService';
import { TaskDraftDTO, TaskDraftStatus, TaskDraftUpdatePayload } from '../../types/TaskDraftDTO';
import { TaskUserDTO } from '../../types/task-user'; 
import { ChatScope, ChatType } from '../../types/chat-enums';
import { useAuth } from '../../contexts/AuthContext';
import { UserType, ManagerRole } from '../../types/auth'; // Добавлен ManagerRole для isOrganizationManager
import { Media } from '../../types/media';
import { format } from 'date-fns';
import ru from 'date-fns/locale/ru';
import { AssignmentInd, Description, InsertPhoto, Link as LinkIcon, Edit, Save, Cancel } from '@mui/icons-material'; // Добавлены Edit, Save, Cancel, LinkIcon
import { grey } from '@mui/material/colors';
import { Container } from '@mui/material';
import { Category } from '../../types/category'; // Добавлено
import { categoryService } from '../../services/categoryService'; // Добавлено
import { TaskDraftCategory } from '../../types/TaskDraftCategory'; // Добавлено
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'; // Добавлено
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'; // Добавлено
import { DatePicker } from '@mui/x-date-pickers/DatePicker'; // Добавлено
import { Skill } from '../../types/skill';
// import TaskChatModal from '../../components/chat/TaskChatModal'; // Закомментировано
import styles from './AdminTaskDetailsPage.module.css';
import { ManagerDTO } from '../../types/manager'; // Добавлено
import { managerService } from '../../services/managerService'; // Добавлено
import TaskDecompositionModal from '../../components/task/TaskDecompositionModal'; // Добавлено

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
};

const AdminTaskDetailsPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const { state } = useAuth();
    const authenticatedUser = state.user;
    const currentManagerId = authenticatedUser?.type === UserType.MANAGER ? authenticatedUser.manager?.id : undefined;
    const navigate = useNavigate();

    const [taskDraft, setTaskDraft] = useState<TaskDraftDTO | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [isEditing, setIsEditing] = useState<boolean>(false); // Добавлено для редактирования

    const [editTitle, setEditTitle] = useState<string>('');
    const [editDescription, setEditDescription] = useState<string>('');
    const [editPrice, setEditPrice] = useState<string>('');
    const [editDeadline, setEditDeadline] = useState<Date | null>(null);
    const [editRequirements, setEditRequirements] = useState<string>('');
    const [editCategories, setEditCategories] = useState<Category[]>([]);
    const [availableCategories, setAvailableCategories] = useState<Category[]>([]); // ВОССТАНОВЛЕНО
    const [availableManagers, setAvailableManagers] = useState<ManagerDTO[]>([]); // ВОССТАНОВЛЕНО
    const [editManagerId, setEditManagerId] = useState<string | undefined>(undefined); // Добавлено для выбранного куратора
    const [mediaFiles, setMediaFiles] = useState<Media[]>([]);
    const [editStatus, setEditStatus] = useState<TaskDraftStatus | undefined>(undefined); // Добавлено для редактирования статуса
    const [isDecompositionModalOpen, setIsDecompositionModalOpen] = useState<boolean>(false); // State for modal

    const isPlatformCurator = authenticatedUser?.type === UserType.MANAGER && authenticatedUser.manager?.roles?.includes(ManagerRole.PLATFORM_CURATOR); // Проверяем роль PlatformCurator

    useEffect(() => {
        console.log('AdminTaskDetailsPage: useEffect triggered');
        console.log('AdminTaskDetailsPage: taskDraftId from useParams', id);
        console.log('AdminTaskDetailsPage: currentManagerId from auth context', currentManagerId);
        const fetchTaskDraft = async () => {
            if (!id) {
                setError('Не удалось загрузить детали черновика задачи: ID черновика задачи отсутствует.');
                setLoading(false);
                console.log('AdminTaskDetailsPage: Missing ID, not fetching task draft');
                return;
            }
            try {
                setLoading(true);
                console.log('AdminTaskDetailsPage: Fetching task draft with ID', id);
                const taskDraftData = await taskService.getTaskDraftById(id);
                setTaskDraft(taskDraftData);
                setEditTitle(taskDraftData.title);
                setEditDescription(taskDraftData.description);
                setEditPrice(taskDraftData.priceInKopecks ? (taskDraftData.priceInKopecks / 100).toString() : '');
                setEditDeadline(taskDraftData.deadline ? new Date(taskDraftData.deadline) : null);
                setEditRequirements(taskDraftData.requirements || '');
                setEditCategories(taskDraftData.taskDraftCategories?.map(tdc => tdc.category) || []);
                setMediaFiles(taskDraftData.media || []);
                setEditStatus(taskDraftData.status);

                const categoriesResponse = await categoryService.getAllCategories();
                setAvailableCategories(categoriesResponse.data);

                const managersResponse = await managerService.getAllManagers();
                setAvailableManagers(managersResponse);
                setEditManagerId(taskDraftData.managerId || undefined); // Устанавливаем текущего куратора

                console.log('AdminTaskDetailsPage: Task draft fetched successfully', taskDraftData);
            } catch (err) {
                setError('Не удалось загрузить детали черновика задачи.');
                console.error('AdminTaskDetailsPage: Error fetching task draft', err);
            } finally {
                setLoading(false);
                console.log('AdminTaskDetailsPage: Task draft fetch finished');
            }
        };

        fetchTaskDraft();
    }, [id, authenticatedUser]);

    const handleSave = async () => {
        if (!taskDraft || !id) return;
        try {
            const updatedTaskDraft: TaskDraftUpdatePayload = {
                id: taskDraft.id, // ID is always needed for PUT
                title: editTitle,
                description: editDescription,
                priceInKopecks: editPrice ? parseFloat(editPrice) * 100 : 0,
                deadline: editDeadline ? editDeadline.toISOString() : undefined,
                requirements: editRequirements,
                managerId: editManagerId === '' ? undefined : editManagerId,
                organizationId: taskDraft.organizationId, // Assuming this is not editable on this page
                estimatedHours: taskDraft.estimatedHours,
                neededUsers: taskDraft.neededUsers,
                priority: taskDraft.priority,
                status: editStatus, // Используем editStatus для сохранения
                updatedAt: new Date().toISOString(),
                taskDraftCategories: editCategories.map(c => ({ categoryId: c.id!, taskDraftId: id! })), // Map to only include IDs
            };

            await taskService.updateTaskDraft(id, updatedTaskDraft);
            // After successful update, fetch the task draft again to get the full updated object with manager details
            const refreshedTaskDraft = await taskService.getTaskDraftById(id);
            setTaskDraft(refreshedTaskDraft);
            setIsEditing(false);
        } catch (err) {
            setError('Не удалось сохранить черновик задачи.');
            console.error(err);
        }
    };

    const handleCancelEdit = () => {
        if (taskDraft) {
            setEditTitle(taskDraft.title);
            setEditDescription(taskDraft.description);
            setEditPrice(taskDraft.priceInKopecks ? (taskDraft.priceInKopecks / 100).toString() : '');
            setEditDeadline(taskDraft.deadline ? new Date(taskDraft.deadline) : null);
            setEditRequirements(taskDraft.requirements || '');
            setEditCategories(taskDraft.taskDraftCategories?.map(tdc => tdc.category) || []);
            setEditManagerId(taskDraft.managerId || undefined); // Сбрасываем выбранного куратора при отмене
            setEditStatus(taskDraft.status);
        }
        setIsEditing(false);
    };

    const handleAskQuestion = () => {
        console.log('Ask question to curator');
    };

    const getStatusDisplayName = (status?: TaskDraftStatus) => {
        switch (status) {
            case TaskDraftStatus.IN_PROCESS: return 'В процессе';
            case TaskDraftStatus.REJECTED: return 'Отклонено';
            case TaskDraftStatus.FOR_REVISION: return 'На доработку';
            case TaskDraftStatus.READY: return 'Готово';
            case TaskDraftStatus.PAID: return 'Оплачено';
            default: return status || 'Неизвестно';
        }
    };

    const getStatusChipColor = (status?: TaskDraftStatus) => {
        switch (status) {
            case TaskDraftStatus.PAID:
            case TaskDraftStatus.READY:
                return "success";
            case TaskDraftStatus.FOR_REVISION:
                return "warning";
            case TaskDraftStatus.REJECTED:
                return "error";
            case TaskDraftStatus.IN_PROCESS:
                return "info";
            default:
                return "default";
        }
    };

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
    }

    if (error) {
        return <Box sx={{ mt: 4 }}><Alert severity="error">{error}</Alert></Box>;
    }

    if (!taskDraft) {
        return <Box sx={{ mt: 4 }}><Alert severity="info">Черновик задачи не найден.</Alert></Box>;
    }

    // Логика для сбора уникальных навыков из taskDraft.skills и taskDraft.tasks
    const uniqueSkills = new Map<string, Skill>();
    if (taskDraft.skills) {
        taskDraft.skills.forEach(s => {
            if (s.id && !uniqueSkills.has(s.id)) {
                uniqueSkills.set(s.id, s);
            }
        });
    }
    if (taskDraft.tasks) {
        taskDraft.tasks.forEach(task => {
            task.taskSkills?.forEach(ts => {
                if (ts.skill && ts.skill.id && !uniqueSkills.has(ts.skill.id)) {
                    uniqueSkills.set(ts.skill.id, ts.skill);
                }
            });
            task.taskRequiredSkills?.forEach(trs => {
                if (trs.skill && trs.skill.id && !uniqueSkills.has(trs.skill.id)) {
                    uniqueSkills.set(trs.skill.id, trs.skill);
                }
            });
        });
    }
    const skillsToDisplay = Array.from(uniqueSkills.values());

    return (
        <Box sx={{ p: 3, maxWidth: 1200, margin: 'auto' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Box>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>Дата создания: {taskDraft.createdAt ? format(new Date(taskDraft.createdAt), 'dd.MM.yyyy', { locale: ru }) : '—'}</Typography>
                    {isEditing ? (
                        <TextField
                            fullWidth
                            variant="outlined"
                            label="Название задачи"
                            value={editTitle}
                            onChange={(e) => setEditTitle(e.target.value)}
                            sx={{ mb: 2 }}
                        />
                    ) : (
                        <Typography variant="h4" component="h1" gutterBottom>{taskDraft.title}</Typography>
                    )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {isEditing ? (
                        <TextField
                            label="Бюджет в рублях"
                            type="number"
                            value={editPrice}
                            onChange={(e) => setEditPrice(e.target.value)}
                            sx={{ mr: 2, width: 150 }}
                        />
                    ) : (
                        <Typography variant="h5" component="span" sx={{ mr: 2 }}>{taskDraft.priceInKopecks ? `${taskDraft.priceInKopecks / 100}₽` : '—'}</Typography>
                    )}
                    {isPlatformCurator && !isEditing && (
                        <Button variant="contained" startIcon={<Edit />} onClick={() => setIsEditing(true)}>Редактировать задачу</Button>
                    )}
                    {isPlatformCurator && !isEditing && (
                        <Button variant="contained" sx={{ ml: 2 }} onClick={() => setIsDecompositionModalOpen(true)}>Декомпозировать задачу</Button>
                    )}
                    {isPlatformCurator && isEditing && (
                        <Box>
                            <Button variant="outlined" startIcon={<Cancel />} onClick={handleCancelEdit} sx={{ mr: 1 }}>Отмена</Button>
                            <Button variant="contained" startIcon={<Save />} onClick={handleSave}>Сохранить Изменения</Button>
                        </Box>
                    )}
                </Box>
                    </Box>
                    
            <Paper elevation={1} sx={{ p: 3, mb: 4 }}>
                <Grid container spacing={2}>
                    <Grid item xs={2}>
                        <Typography variant="body2" color="textSecondary">Категория</Typography>
                        {isEditing ? (
                            <FormControl fullWidth size="small">
                                <InputLabel id="edit-categories-label">Категория</InputLabel>
                                <Select
                                    labelId="edit-categories-label"
                                    multiple
                                    value={editCategories.map(c => c.id)}
                                    onChange={(event) => {
                                        const value = event.target.value as string[];
                                        setEditCategories(availableCategories.filter(c => value.includes(c.id!)));
                                    }}
                                    input={<OutlinedInput id="select-edit-categories-chip" label="Категория" />}
                                    renderValue={(selected) => (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {selected.map((value) => {
                                                const category = availableCategories.find(c => c.id === value);
                                                return <Chip key={value} label={category?.title || value} />;
                                            })}
                        </Box>
                                    )}
                                    MenuProps={MenuProps}
                                >
                                    {availableCategories.map((category) => (
                                        <MenuItem key={category.id} value={category.id}>
                                            {category.title}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        ) : (
                            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                {taskDraft.taskDraftCategories && taskDraft.taskDraftCategories.length > 0 ? (
                                    taskDraft.taskDraftCategories.map(tdc => <Chip key={tdc.category.id} label={tdc.category.title} />)
                                ) : (
                                    <Typography variant="subtitle1">—</Typography>
                                )}
                            </Stack>
                        )}
                    </Grid>
                    <Grid item xs={2}>
                        <Typography variant="body2" color="textSecondary">Статус</Typography>
                        {isEditing ? (
                            <FormControl fullWidth size="small">
                                <InputLabel id="edit-status-label">Статус</InputLabel>
                                <Select
                                    labelId="edit-status-label"
                                    value={editStatus || ''}
                                    label="Статус"
                                    onChange={(event) => setEditStatus(event.target.value as TaskDraftStatus)}
                                >
                                    {Object.values(TaskDraftStatus).map((status) => (
                                        <MenuItem key={status} value={status}>
                                            {getStatusDisplayName(status)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        ) : (
                            <Typography variant="subtitle1">{getStatusDisplayName(taskDraft.status)}</Typography>
                        )}
                    </Grid>
                    <Grid item xs={2}>
                        <Typography variant="body2" color="textSecondary">Принятие</Typography>
                        <Typography variant="subtitle1">{taskDraft.createdAt ? format(new Date(taskDraft.createdAt), 'dd.MM.yyyy') : '—'} {/* Placeholder */}</Typography>
                    </Grid>
                    <Grid item xs={2}>
                        <Typography variant="body2" color="textSecondary">Дедлайн</Typography>
                        {isEditing ? (
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <DatePicker
                                    label="Дедлайн"
                                    value={editDeadline}
                                    onChange={(newValue) => setEditDeadline(newValue)}
                                    slots={{ textField: (params) => <TextField {...params} fullWidth size="small" /> }} 
                                />
                            </LocalizationProvider>
                        ) : (
                            <Typography variant="subtitle1">{taskDraft.deadline ? format(new Date(taskDraft.deadline), 'dd.MM.yyyy', { locale: ru }) : '—'}</Typography>
                        )}
                    </Grid>
                    <Grid item xs={4}>
                        <Typography variant="body2" color="textSecondary">Заказчик</Typography>
                        <Typography variant="subtitle1">{taskDraft.organization?.name || '—'}</Typography>
                    </Grid>
                </Grid>
                    </Paper>
                    
            {/* Task Description Section */}
            <Box sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom>Описание задачи:</Typography>
                {isEditing ? (
                    <TextField
                        fullWidth
                        multiline
                        rows={6}
                        variant="outlined"
                        label="Описание задачи"
                        value={editDescription}
                        onChange={(e) => setEditDescription(e.target.value)}
                    />
                ) : (
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>{taskDraft.description}</Typography>
                )}
                    </Box>
                    
            {/* Requirements Section */}
            {taskDraft.requirements && (
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h6" gutterBottom>Требования:</Typography>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>{taskDraft.requirements}</Typography>
                </Box>
            )}
                    
                    {/* Skills Section */}
            <Box sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom>Скиллы для задачи:</Typography>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    {skillsToDisplay.length > 0 ? (
                        skillsToDisplay.map(skill => <Chip key={skill.id} label={skill.title} />)
                    ) : (
                        <Typography variant="body2" color="textSecondary">Навыки не указаны.</Typography>
                    )}
                </Stack>
            </Box>

            {/* Uploaded Files Section */}
            <Box sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom>Загруженные файлы:</Typography>
                <Stack spacing={1}>
                    {mediaFiles.length > 0 ? (
                        mediaFiles.map((mediaItem) => (
                            <Paper key={mediaItem.id} variant="outlined" sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                                {mediaItem.fileUrl.match(/\.(jpeg|jpg|gif|png|webp)$/i) ? (
                                    <img src={mediaItem.fileUrl} alt={mediaItem.name} style={{ width: 24, height: 24, objectFit: 'cover' }} />
                                ) : (
                                    <LinkIcon fontSize="small" />
                                )}
                                <Typography variant="body2" sx={{ flexGrow: 1 }}>
                                    <a href={mediaItem.fileUrl} target="_blank" rel="noopener noreferrer">{mediaItem.name}</a>
                                </Typography>
                                </Paper>
                        ))
                    ) : (
                        <Typography variant="body2" color="textSecondary">Файлы не загружены.</Typography>
                    )}
                </Stack>
            </Box>

            {/* Curator Section */}
            <Paper elevation={1} sx={{ p: 3, mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar src={taskDraft.manager?.avatar} sx={{ mr: 2 }} />
                    <Box>
                        <Typography variant="body2" color="textSecondary">Куратор</Typography>
                        <Typography variant="subtitle1">{taskDraft.manager?.fullName || 'Не указан'}</Typography>
                    </Box>
                </Box>
                {isEditing ? (
                    <FormControl size="small" sx={{ width: 200 }}>
                        <InputLabel id="edit-curator-label">Куратор</InputLabel>
                        <Select
                            labelId="edit-curator-label"
                            value={editManagerId || ''}
                            label="Куратор"
                            onChange={(event) => setEditManagerId(event.target.value as string)}
                        >
                            <MenuItem value="">—</MenuItem>
                            {availableManagers.map((manager) => (
                                <MenuItem key={manager.id} value={manager.id}>
                                    {manager.fullName}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                ) : (
                    <Button variant="contained" onClick={handleAskQuestion}>ЗАДАТЬ ВОПРОС ПО ЗАДАЧЕ</Button>
                )}
            </Paper>

            {/* Декомпозированные задачи и их исполнители */}
            {taskDraft.tasks && taskDraft.tasks.length > 0 && (
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h6" gutterBottom>Декомпозированные задачи:</Typography>
                    <Stack direction="column" spacing={2}>
                        {taskDraft.tasks.map(task => (
                            <Paper key={task.id} elevation={1} sx={{ p: 2 }}>
                                <Typography variant="h6" gutterBottom>Задача: {task.title}</Typography>
                                <Grid container spacing={1} sx={{ mb: 1 }}>
                                    <Grid item xs={3}>
                                        <Typography variant="body2" color="textSecondary">Дедлайн:</Typography>
                                        <Typography variant="subtitle2">{task.deadline ? format(new Date(task.deadline), 'dd.MM.yyyy', { locale: ru }) : '—'}</Typography>
                                    </Grid>
                                    <Grid item xs={3}>
                                        <Typography variant="body2" color="textSecondary">Статус:</Typography>
                                        <Typography variant="subtitle2">{task.status}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="body2" color="textSecondary">Бюджет:</Typography>
                                        <Typography variant="subtitle2">{task.priceInKopecks ? `${task.priceInKopecks / 100}₽` : '—'}</Typography>
                                    </Grid>
                                </Grid>
                                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                                    <Button 
                                        variant="outlined" 
                                        color="primary"
                                        onClick={() => navigate(`/admin/task-entity/${task.id}`)}
                                    >
                                        Просмотреть/Редактировать
                                    </Button>
                                </Box>
                                <Typography variant="subtitle1" gutterBottom>Исполнители:</Typography>
                                <Stack direction="row" spacing={2} sx={{ overflowX: 'auto', pb: 1 }}>
                                    {task.taskUsers && task.taskUsers.length > 0 ? (
                                        task.taskUsers.map(taskUser => (
                                            <Paper key={taskUser.id} elevation={1} sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 150 }}>
                                                <Avatar src={taskUser.user?.avatar} sx={{ width: 56, height: 56, mb: 1 }}>
                                                    {taskUser.user?.fullName ? taskUser.user.fullName.split(' ').map(n => n[0]).join('') : (taskUser.user?.personalData?.fullName ? taskUser.user.personalData.fullName.split(' ').map(n => n[0]).join('') : '—')}
                                                </Avatar>
                                                <Typography variant="subtitle2">{taskUser.user?.fullName || taskUser.user?.personalData?.fullName || '—'}</Typography>
                                                <Typography variant="body2" color="textSecondary">{taskUser.status}</Typography>
                                            </Paper>
                                        ))
                                    ) : (
                                        <Typography variant="body2" color="textSecondary">Исполнители не назначены.</Typography>
                                    )}
                                </Stack>
                            </Paper>
                        ))}
                    </Stack>
                </Box>
            )}

            {isPlatformCurator && isEditing && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                    <Button variant="outlined" startIcon={<Cancel />} onClick={handleCancelEdit}>Отмена</Button>
                    <Button variant="contained" startIcon={<Save />} onClick={handleSave}>Сохранить Изменения</Button>
                </Box>
            )}
            {taskDraft && (
                <TaskDecompositionModal
                    open={isDecompositionModalOpen}
                    onClose={() => setIsDecompositionModalOpen(false)}
                    taskDraft={taskDraft}
                    onDecomposeSuccess={() => {
                        setIsDecompositionModalOpen(false);
                        // Optionally refresh the task draft or navigate away
                        // For now, let's just close the modal
                    }}
                />
            )}
        </Box>
    );
};

export default AdminTaskDetailsPage;
